/**
 * MerchantUserAdminPage — iter-259 (Feb 2026)
 * ==========================================
 * Full-page (NOT drawer) admin console for a single merchant account.
 * Replaces `MerchantUserAdminDrawer` which was overflowing the sidebar
 * on smaller viewports.
 *
 * Route: /manage-platform/merchant-users/:userId?email=<email>
 *
 * Lifecycle enforced by the backend (block → archive → delete):
 *   1) Block:    POST /admin/merchant-users/:id/block  (body: {reason})
 *   2) Archive:  POST /admin/merchant-users/:id/archive
 *   3) Delete:   DELETE /admin/merchant-users/:id/cascade
 *
 * Unblock is blocked while the account is archived — the operator must
 * restore first.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Loader2, ShieldCheck, ShieldOff, Ban,
  BadgeCheck, XCircle, Mail, Phone, AlertTriangle,
  User as UserIcon, Link2, Key, Archive, ArrowLeft, ArrowRight,
  RefreshCw, Building2, Wallet, TrendingUp, Clock, Receipt,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MerchantUserAdminPage() {
  const { userId } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = useCallback((ar, en) => (isAr ? ar : en), [isAr]);

  const emailFromQuery = (sp.get('email') || '').trim();

  const [user, setUser] = useState(null);
  const [archived, setArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState('');

  // iter-288 — Agent commission wallet (loaded when the viewed user is an agent).
  const [wallet, setWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);

  // iter-269 — removed showBlockForm / blockReason since block controls
  // are no longer on this page (moved to the outer list).

  const loadUser = useCallback(async () => {
    setLoading(true); setNotFound(false);
    const attempts = [];
    if (emailFromQuery) attempts.push(`/admin/merchant-users/by-email/${encodeURIComponent(emailFromQuery)}`);
    if (userId)          attempts.push(`/admin/merchant-users/${userId}`);
    for (const path of attempts) {
      try {
        const r = await axios.get(`${API}${path}`, { headers: getAuthHeaders() });
        setUser(r.data || null);
        // Check archive state locally
        try {
          const arch = await axios.get(`${API}/admin/merchant-users/archived`, { headers: getAuthHeaders() });
          const rows = arch.data?.data || [];
          const uid = (r.data?.id) || userId;
          setArchived(rows.some((row) => row.user_id === uid));
        } catch (_e) { setArchived(false); }
        setLoading(false); return;
      } catch (_err) { /* try next */ }
    }
    setUser(null); setNotFound(true); setLoading(false);
  }, [userId, emailFromQuery]);

  useEffect(() => { loadUser(); }, [loadUser]);

  // iter-288 — Load wallet whenever the user resolves. We attempt the lookup
  // on EVERY merchant since any merchant may hold a commission balance from
  // past company registrations; the endpoint is cheap and returns zeros for
  // non-agents.
  useEffect(() => {
    if (!user?.email) return;
    let cancelled = false;
    (async () => {
      setWalletLoading(true);
      try {
        const r = await axios.get(
          `${API}/admin/agents/wallet-by-email/${encodeURIComponent(user.email)}${user.id ? `?user_id=${encodeURIComponent(user.id)}` : ''}`,
          { headers: getAuthHeaders() },
        );
        if (!cancelled) setWallet(r.data?.data || null);
      } catch (_e) { if (!cancelled) setWallet(null); }
      finally { if (!cancelled) setWalletLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [user?.email, user?.id]);

  const isBlocked = !!user?.is_banned;

  const forceUnlink = async (provider) => {
    if (!window.confirm(t(`فك ربط ${provider}؟`, `Force-unlink ${provider}?`))) return;
    setBusy(`unlink-${provider}`);
    try {
      await axios.delete(`${API}/admin/merchant-users/${user.id}/linked-providers/${provider}`,
                          { headers: getAuthHeaders() });
      toast.success(t('تم فك الربط', 'Unlinked'));
      await loadUser();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setBusy(''); }
  };

  // iter-269 — All account state actions (block/unblock/archive/restore/delete)
  // have been moved out of this page. They live exclusively on the outer
  // accounts list and the archive page. This page is now READ-ONLY for
  // account state; the only mutation it still owns is force-unlink of
  // Google/Apple providers, which is legitimately per-account.

  const breadcrumb = useMemo(() => [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'CLOUDGM FZ LLC', label_en: 'CLOUDGM FZ LLC', path: '/manage/cloudgm-business' },
    { label_ar: 'منصة التجار', label_en: 'Merchant Platform', path: '/manage/cloudgm-business' },
    { label_ar: 'الحسابات', label_en: 'Accounts', path: '/manage/cloudgm-business?tab=accounts' },
    { label_ar: 'إدارة الحساب', label_en: 'Manage Account', path: null },
  ], []);

  const backToList = () => navigate('/manage/cloudgm-business?tab=accounts');

  return (
    <div className="min-h-screen bg-[#030303] text-white" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      <DashboardHeader onBack={backToList} />
      <div className="border-b border-white/5 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={breadcrumb} />
        </div>
      </div>

      <main className="relative z-10 py-6 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
            <div className="flex items-center gap-3">
              <Button
                onClick={backToList} variant="outline" size="sm"
                className="border-white/10 text-white/70 hover:bg-white/5"
                data-testid="merchant-page-back"
              >
                {isAr ? <ArrowRight className="h-4 w-4 me-2" /> : <ArrowLeft className="h-4 w-4 me-2" />}
                {t('رجوع للقائمة', 'Back to list')}
              </Button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-white flex items-center gap-2" data-testid="merchant-page-title">
                  <Building2 className="h-5 w-5 text-[#d4af37]" />
                  {t('إدارة حساب التاجر', 'Manage Merchant Account')}
                </h1>
                <p className="text-[11px] text-white/40 mt-0.5" dir="ltr">
                  {user?.email || emailFromQuery || '—'}
                </p>
              </div>
            </div>
            <Button onClick={loadUser} variant="outline" size="sm"
                    className="border-white/10 text-white/70 hover:bg-white/5" data-testid="merchant-page-refresh">
              <RefreshCw className="h-4 w-4 me-2" />
              {t('تحديث', 'Refresh')}
            </Button>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {[0,1,2,3].map((i) => (
                <div key={i} className="rounded-2xl bg-white/[0.02] border border-white/10 p-5 h-40 animate-pulse" />
              ))}
            </div>
          ) : notFound ? (
            <div className="rounded-2xl border border-red-500/30 bg-red-500/[0.03] p-6" data-testid="merchant-page-not-found">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-6 w-6 text-red-400" />
                <div>
                  <h2 className="text-base font-bold text-white">{t('الحساب غير موجود', 'Account not found')}</h2>
                  <p className="text-[12px] text-white/60 mt-1" dir="ltr">
                    {emailFromQuery || userId}
                  </p>
                </div>
              </div>
            </div>
          ) : user ? (
            <div className="space-y-5">
              {/* iter-267 — Local-only banner: source AI-Core service has
                  no record for this merchant but we have a local snapshot
                  in our shared DB. Block/archive/delete still work — they
                  operate on the local doc directly. */}
              {user._local_only && (
                <div className="rounded-xl border border-sky-500/30 bg-sky-500/[0.06] p-4 flex items-start gap-3"
                     data-testid="merchant-page-local-fallback-banner">
                  <AlertTriangle className="h-4 w-4 text-sky-300 mt-0.5" />
                  <div className="flex-1 text-[12px] text-sky-100 leading-relaxed">
                    <p className="font-semibold mb-1">
                      {t('حساب محلي فقط', 'Local-only account')}
                    </p>
                    <p className="text-white/70">
                      {t(
                        'لم يُسجّل هذا التاجر على منصة CloudGM Business عن بُعد بعد. الحظر والأرشفة والحذف تعمل من هنا، لكن الربط بـ Google/Apple لن يظهر حتى يسجّل التاجر على المنصة.',
                        'This merchant is not yet registered on the remote CloudGM Business service. Block/archive/delete still work locally, but Google/Apple linking will show once the merchant signs up on the platform.',
                      )}
                    </p>
                  </div>
                </div>
              )}

              {/* Status banner if archived — iter-269 read-only, no restore button here */}
              {archived && (
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 flex items-center gap-3"
                     data-testid="merchant-page-archived-banner">
                  <Archive className="h-4 w-4 text-amber-300" />
                  <div className="flex-1 text-[12px] text-amber-200">
                    {t(
                      'هذا الحساب في الأرشيف. لاستعادته توجّه إلى صفحة الأرشيف.',
                      'This account is in the archive. Restore it from the archive page.',
                    )}
                  </div>
                </div>
              )}

              {/* Identity + status grid */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <section className="lg:col-span-2 bg-white/[0.02] border border-white/10 rounded-2xl p-5"
                         data-testid="merchant-page-identity">
                  <h3 className="text-[11px] uppercase text-white/40 mb-4 flex items-center gap-2">
                    <UserIcon className="h-3 w-3" /> {t('الهوية', 'Identity')}
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-[12px]">
                    <Field label={t('الاسم', 'Name')} value={user.name || `${user.first_name||''} ${user.last_name||''}`.trim() || '—'} />
                    <Field label={t('البريد', 'Email')} value={user.email} dir="ltr" />
                    <Field label={t('الهاتف', 'Phone')} value={user.phone} dir="ltr" />
                    <Field label={t('المنصة', 'Platform')} value={user.platform || 'cloudgm_business'} />
                  </div>
                </section>

                <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-5"
                         data-testid="merchant-page-verification">
                  <h3 className="text-[11px] uppercase text-white/40 mb-4">{t('التحقق', 'Verification')}</h3>
                  <div className="flex flex-col gap-2 text-[12px]">
                    <VerifyRow ok={user.email_verified} label={t('البريد', 'Email')} icon={Mail} />
                    <VerifyRow ok={user.phone_verified} label={t('الهاتف', 'Phone')} icon={Phone} />
                    <VerifyRow ok={user.has_password} label={t('كلمة المرور', 'Password')} icon={Key} />
                  </div>
                </section>
              </div>

              {/* Sign-in methods */}
              <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-5"
                       data-testid="merchant-page-signin-methods">
                <h3 className="text-[11px] uppercase text-white/40 mb-4">{t('طرق تسجيل الدخول', 'Sign-in Methods')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <ProviderRow brand="Google" brandColor="#EA4335"
                    data={user.linked_providers?.google}
                    onUnlink={() => forceUnlink('google')}
                    busy={busy === 'unlink-google'} isAr={isAr} testIdKey="google" />
                  <ProviderRow brand="Apple" brandColor="#ffffff"
                    data={user.linked_providers?.apple}
                    onUnlink={() => forceUnlink('apple')}
                    busy={busy === 'unlink-apple'} isAr={isAr} testIdKey="apple" />
                </div>
              </section>

              {/* iter-288 — Agent Commission Wallet (available / pending /
                  lifetime). Shows loader while fetching; renders zeros for
                  non-agent merchants so the operator always has visibility. */}
              <section className="rounded-2xl p-5 border bg-gradient-to-br from-[#d4af37]/[0.06] to-amber-500/[0.02] border-[#d4af37]/25"
                       data-testid="merchant-page-wallet-card">
                <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
                  <div className="flex items-center gap-2.5">
                    <div className="h-9 w-9 rounded-xl bg-[#d4af37]/15 border border-[#d4af37]/30 flex items-center justify-center">
                      <Wallet className="h-4 w-4 text-[#d4af37]" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-white">
                        {t('محفظة العمولات', 'Commission Wallet')}
                      </h3>
                      <p className="text-[10px] text-white/40 mt-0.5">
                        {t('الرصيد المتوفر لدفع فواتير ASB606 أو السحب', 'Available balance for ASB606 invoices or withdrawal')}
                      </p>
                    </div>
                  </div>
                  {walletLoading && <Loader2 className="h-3.5 w-3.5 text-[#d4af37] animate-spin" />}
                </div>

                {wallet && (wallet.negative_flag) && (
                  <div className="mb-3 rounded-lg border border-red-500/30 bg-red-500/[0.06] px-3 py-2 text-[11px] text-red-200">
                    {t(
                      '⚠️ خطأ في الدفتر — الرصيد المحسوب أقل من صفر. راجع سجل الحركات.',
                      '⚠️ Ledger discrepancy — computed balance is negative. Review ledger entries.',
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <WalletTile
                    icon={Wallet}
                    label={t('متوفر الآن', 'Available Now')}
                    value={wallet?.available}
                    highlight
                    testId="wallet-available"
                  />
                  <WalletTile
                    icon={Clock}
                    label={t('قيد السحب', 'Pending Withdrawals')}
                    value={wallet?.pending_withdrawals}
                    testId="wallet-pending"
                  />
                  <WalletTile
                    icon={TrendingUp}
                    label={t('إجمالي المكتسب', 'Lifetime Earned')}
                    value={wallet?.lifetime_earned}
                    testId="wallet-lifetime-earned"
                  />
                  <WalletTile
                    icon={Receipt}
                    label={t('مدفوع لفواتير ASB606', 'Paid to ASB606 Invoices')}
                    value={wallet?.lifetime_invoice_paid}
                    testId="wallet-lifetime-invoice-paid"
                  />
                </div>

                {wallet?.ledger && wallet.ledger.length > 0 && (
                  <div className="mt-4 rounded-lg border border-white/5 bg-black/20 p-3">
                    <p className="text-[10px] uppercase text-white/40 mb-2">
                      {t('آخر الحركات', 'Recent Ledger Entries')}
                    </p>
                    <div className="divide-y divide-white/5" data-testid="wallet-ledger-list">
                      {wallet.ledger.slice(0, 6).map((l) => (
                        <div key={l.id} className="flex items-center justify-between gap-2 py-2 text-[11px]">
                          <div className="min-w-0 flex-1">
                            <p className="text-white/80 truncate">
                              {l.kind === 'invoice_debit'
                                ? t('خصم لدفع فاتورة', 'Invoice payment')
                                : l.kind}
                              {l.invoice_no && <span className="text-white/40 ms-2" dir="ltr">· {l.invoice_no}</span>}
                            </p>
                            <p className="text-white/40 text-[10px] mt-0.5">
                              {l.created_at ? new Date(l.created_at).toLocaleString('en-GB', {
                                day: '2-digit', month: 'short', year: 'numeric',
                                hour: '2-digit', minute: '2-digit',
                              }) : '—'}
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-red-300 font-semibold" dir="ltr">
                              - {Number(l.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>

              {/* iter-269 — READ-ONLY block/archive status card.
                  All actions (block/unblock/archive/restore/delete)
                  are performed exclusively from the outer list and the
                  archive page. This page just shows the current state. */}
              <section className={`rounded-2xl p-5 border ${isBlocked
                ? 'bg-red-500/5 border-red-500/30' : 'bg-emerald-500/5 border-emerald-500/20'}`}
                data-testid="merchant-page-block-card">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <h3 className={`text-sm font-semibold flex items-center gap-2 ${isBlocked ? 'text-red-300' : 'text-emerald-300'}`}>
                      {isBlocked ? <Ban className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
                      {isBlocked ? t('الحساب محظور', 'Blocked') : t('الحساب فعّال', 'Active')}
                      {archived && (
                        <span className="ms-2 text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">
                          {t('في الأرشيف', 'In archive')}
                        </span>
                      )}
                    </h3>
                    {isBlocked && user.ban_reason && (
                      <p className="text-[12px] text-red-200 mt-1">
                        {t('السبب: ', 'Reason: ')}"{user.ban_reason}"
                      </p>
                    )}
                    <p className="text-[10px] text-white/40 mt-2 leading-relaxed">
                      {t(
                        'الحظر والأرشفة يتمّان من صفحة الحسابات الخارجية. الحذف من صفحة الأرشيف فقط.',
                        'Block / archive actions live on the outer accounts list. Delete lives on the archive page only.',
                      )}
                    </p>
                  </div>
                </div>
              </section>
            </div>
          ) : null}
        </div>
      </main>
    </div>
  );
}

/* ── Primitives ─────────────────────────────────────────────────── */

const Field = ({ label, value, dir }) => (
  <div>
    <p className="text-[10px] text-white/40 mb-0.5">{label}</p>
    <p className="text-white/85 truncate text-[13px]" dir={dir || undefined}>{value || '—'}</p>
  </div>
);

/* ── iter-288 — Wallet tile for the Commission Wallet section ─────── */
const WalletTile = ({ icon: Icon, label, value, highlight, testId }) => {
  const amount = (value == null || Number.isNaN(Number(value)))
    ? '—'
    : Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div className={`rounded-xl border p-3 ${highlight
      ? 'bg-[#d4af37]/[0.1] border-[#d4af37]/40'
      : 'bg-white/[0.02] border-white/8'}`}
      data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className={`h-3 w-3 ${highlight ? 'text-[#d4af37]' : 'text-white/50'}`} />
        <p className="text-[9px] uppercase tracking-wider text-white/50">{label}</p>
      </div>
      <p className={`text-[15px] font-bold tabular-nums ${highlight ? 'text-[#d4af37]' : 'text-white/90'}`} dir="ltr">
        {amount}
        <span className="text-[9px] text-white/40 ms-1">AED</span>
      </p>
    </div>
  );
};

const VerifyRow = ({ ok, label, icon: Icon }) => (
  <div className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2
    ${ok ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-white/[0.02] border-white/5'}`}>
    <div className="flex items-center gap-2">
      <Icon className={`h-3 w-3 ${ok ? 'text-emerald-400' : 'text-white/40'}`} />
      <span className="text-white/85">{label}</span>
    </div>
    {ok
      ? <BadgeCheck className="h-3.5 w-3.5 text-emerald-400" />
      : <XCircle className="h-3.5 w-3.5 text-white/30" />}
  </div>
);

const ProviderRow = ({ brand, brandColor, data, onUnlink, busy, isAr, testIdKey }) => {
  const linked = !!(data && data.linked);
  const linkedDate = data?.linked_at
    ? (() => {
        try { return new Date(data.linked_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
        catch { return ''; }
      })()
    : '';
  return (
    <div className={`rounded-lg border px-3 py-2 text-[12px] flex flex-col gap-1
      ${linked ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-black/30 border-white/5'}`}
      data-testid={`merchant-page-provider-${testIdKey}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Link2 className="h-3 w-3" style={{ color: linked ? '#34d399' : '#71717a' }} />
          <span className="font-semibold" style={{ color: brandColor }}>{brand}</span>
          <span className="text-white/60 truncate" dir="ltr">
            {linked ? `${data.provider_email || (isAr ? '(بريد مخفي)' : '(hidden)')}` : (isAr ? 'غير مرتبط' : 'not linked')}
          </span>
        </div>
        {linked && (
          <button onClick={onUnlink} disabled={busy}
                  className="h-7 w-7 rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-400 flex items-center justify-center disabled:opacity-50 shrink-0"
                  data-testid={`merchant-page-unlink-${testIdKey}`}
                  title={isAr ? `فك ربط ${brand}` : `Force-unlink ${brand}`}>
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldOff className="h-3 w-3" />}
          </button>
        )}
      </div>
      {linked && (linkedDate || data?.provider_uid) && (
        <div className="flex items-center gap-3 pl-5 text-[10px] text-white/40">
          {linkedDate && (
            <span>{isAr ? 'مربوط منذ' : 'Linked'} {linkedDate}</span>
          )}
          {data?.provider_uid && (
            <span className="font-mono truncate" dir="ltr" title={data.provider_uid}>
              uid: {data.provider_uid}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
